import { useEffect, useState } from 'react';
import { Loader2, Pencil, Shapes, Trash2 } from 'lucide-react';
import espnowService, { type RelayZone } from '../../../services/espnowIrrigation';
import type { RelaySlot } from '../../../utils/relayCatalogue';
import { errorMessage } from '../../../utils/apiResponse';

interface Props {
  slot: RelaySlot;
  editable: boolean;
  /** True while the map is in draw mode for THIS relay. */
  drawing: boolean;
  onDraw: () => void;
  onCancelDraw: () => void;
  /** Null when the zone was deleted. */
  onZoneSaved: (zone: RelayZone | null, routerMac: string, relayIndex: number) => void;
}

/**
 * The area a valve waters — reached through its relay, never stored twice.
 *
 * `field_relay_zones` is keyed on `(router_mac, relay_index)`, the same pair the
 * node's relay link carries, so a valve that has a relay already has an area.
 * Giving the node its own zone id would create a second link that could
 * disagree with the first, with nothing to reconcile them: the valve would
 * claim one outline while the relay it actually drives wetted another.
 *
 * Two states that must not be collapsed:
 *
 * - **No zone row at all** — nobody has described this relay's area.
 * - **A zone row with no `boundary_geometry`** — named and coloured, never
 *   drawn. It appears in lists and legends and contributes no polygon, so a UI
 *   that reports it as "no area" leaves the operator looking for something they
 *   already created.
 */
export default function ZoneLinkSection({
  slot, editable, drawing, onDraw, onCancelDraw, onZoneSaved,
}: Props) {
  const zone = slot.zone;
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [color, setColor] = useState('#3B82F6');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setEditing(false);
    setError(null);
    setName(zone?.zone_name ?? '');
    setColor(zone?.zone_color ?? '#3B82F6');
  }, [zone?.id, zone?.zone_name, zone?.zone_color]);

  const save = async (patch: Partial<Pick<RelayZone, 'zone_name' | 'zone_color'>>) => {
    setBusy(true);
    setError(null);
    try {
      const saved = await espnowService.updateRelayZone(
        slot.router_mac, slot.relay_index, slot.controller_id,
        {
          zone_name: patch.zone_name !== undefined ? (patch.zone_name || null) : (zone?.zone_name ?? null),
          zone_color: patch.zone_color ?? zone?.zone_color ?? '#3B82F6',
          // Passed through untouched. Omitting it would clear the outline, and
          // renaming an area is not a reason to lose the shape of it.
          boundary_geometry: zone?.boundary_geometry ?? null,
          notes: zone?.notes ?? null,
        },
      );
      onZoneSaved(saved, slot.router_mac, slot.relay_index);
      setEditing(false);
    } catch (e) {
      setError(errorMessage(e, 'Could not save the area'));
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!confirm('Remove this relay’s area? The outline and its name are deleted.')) return;
    setBusy(true);
    setError(null);
    try {
      await espnowService.deleteRelayZone(slot.router_mac, slot.relay_index, slot.controller_id);
      onZoneSaved(null, slot.router_mac, slot.relay_index);
    } catch (e) {
      setError(errorMessage(e, 'Could not remove the area'));
    } finally { setBusy(false); }
  };

  const hasOutline = !!zone?.boundary_geometry;

  return (
    <div className="mt-1.5 border-t pt-1.5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <span
            className="w-3 h-3 rounded-sm border shrink-0"
            style={{ backgroundColor: zone?.zone_color ?? 'transparent' }}
          />
          <span className="font-medium truncate">
            {zone?.zone_name || (zone ? slot.label : 'No area drawn')}
          </span>
        </div>
        {editable && zone && !editing && (
          <button className="text-blue-600 whitespace-nowrap" onClick={() => setEditing(true)}>
            <Pencil className="w-3 h-3 inline" /> name
          </button>
        )}
      </div>

      {zone && !hasOutline && (
        // Named but never drawn. Reporting this as "no area" sends somebody
        // looking for a zone they already made.
        <div className="text-amber-700 mt-0.5">Named, but no outline has been drawn.</div>
      )}
      {!zone && (
        <div className="text-gray-500 mt-0.5">
          Nothing drawn for this relay yet — the farm map will not show what it waters.
        </div>
      )}

      {editing && (
        <div className="mt-1 flex items-center gap-1">
          <input
            className="input flex-1 min-w-0"
            value={name}
            placeholder={slot.label}
            // The column is varchar(64) and the route's schema says so; without
            // this the refusal arrives as a 400 after the typing is done.
            maxLength={64}
            disabled={busy}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            type="color"
            className="w-7 h-7 border rounded p-0 shrink-0"
            value={color}
            disabled={busy}
            onChange={(e) => setColor(e.target.value)}
          />
          <button
            className="px-2 py-1 rounded bg-blue-600 text-white disabled:opacity-40"
            disabled={busy}
            onClick={() => save({ zone_name: name, zone_color: color })}
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
          </button>
        </div>
      )}

      {editable && (
        <div className="mt-1 flex flex-wrap items-center gap-2">
          {drawing ? (
            <button className="text-gray-600" onClick={onCancelDraw}>
              Drawing on the map — cancel
            </button>
          ) : (
            <button className="text-blue-600 flex items-center gap-1" onClick={onDraw} disabled={busy}>
              <Shapes className="w-3 h-3" /> {hasOutline ? 'Redraw outline' : 'Draw outline'}
            </button>
          )}
          {zone && !drawing && (
            <button className="text-red-600 flex items-center gap-1" onClick={remove} disabled={busy}>
              <Trash2 className="w-3 h-3" /> remove area
            </button>
          )}
        </div>
      )}

      {error && <div className="text-red-600 mt-0.5">{error}</div>}
    </div>
  );
}
